import {
  type JobApplicantAIScreening,
  type JobApplicantResponse,
  JobCandidateStageEnum,
  type JobPostingResponse,
  type LastAppliedJob,
  type PaginatedJobPostingResponseListStatusCounts,
  type TimelineInterview,
  type TimelineJobOffer,
  type TimelineScreeningInterview,
  type WorkedWithYouHistory,
} from "@sidegigllc/api/client";
import type { JobPost } from "api/resources/hire";
import type { ProBadge } from "api/resources/pros";
import type { ApplicantData } from "domains/hire/components/ApplicantSheet";
import type { AvailabilityWindow } from "domains/hire/components/AvailabilityWindowsField";

interface AvailabilityWrite {
  /** The availability sub-form is dirty — the manager touched the field this session. */
  isDirty: boolean;
  /** Current staged override flag: `true` pins the posting, `false` was set by Reset. */
  isOverride: boolean;
  /** Whether the posting already holds its own override, per the server. */
  storedIsOverride: boolean;
}

/**
 * What submitting has to do to the posting's availability, which is a separate resource from the job
 * payload and so cannot ride along in it.
 *
 * Every branch here exists because availability is written *after* the job saves:
 * - `"none"` while untouched, because the submit path runs on every save — re-PUTting an override
 *   nobody edited would bump its `updated_at` on unrelated changes.
 * - `"clear"` only where the server actually holds an override. Reset on a posting that was
 *   inheriting anyway has nothing to delete.
 * - `"save"` for any edit, including one made after a Reset, since an edit always pins the posting.
 */
export const availabilityWriteFor = ({
  isDirty,
  isOverride,
  storedIsOverride,
}: AvailabilityWrite): "save" | "clear" | "none" => {
  if (!isDirty) return "none";
  if (!isOverride) return storedIsOverride ? "clear" : "none";
  return "save";
};

/**
 * Whether two weeks hold the same windows, ignoring row order.
 *
 * The API sorts a week by `(start, days, end)` and neither availability surface offers a way to
 * reorder rows, so position is the manager's arrangement rather than data — a difference in it is
 * not an edit, and not something to report back as a change the server made.
 */
export const sameWindows = (a: Array<AvailabilityWindow>, b: Array<AvailabilityWindow>) => {
  const canonical = (windows: Array<AvailabilityWindow>) =>
    windows
      .map((window) => JSON.stringify(window))
      .toSorted((x, y) => x.localeCompare(y))
      .join("|");
  return canonical(a) === canonical(b);
};

/**
 * How many jobs the business posted itself. A direct hire is a private job it never posted, so it doesn't
 * count. The first-job toast and the first-job welcome modal both key off this, so they can't disagree.
 */
export const postedJobCount = (counts?: Partial<PaginatedJobPostingResponseListStatusCounts>) =>
  (counts?.open ?? 0) + (counts?.filled ?? 0) + (counts?.archived ?? 0) - (counts?.direct_hire ?? 0);

// generated badges include TOP_PRO, ProBadge doesn't
export const dropTopProBadge = (badges?: ReadonlyArray<string>) =>
  badges?.filter((badge): badge is ProBadge[number] => badge !== "TOP_PRO");

export const isLastAppliedJobSchedulable = (job?: LastAppliedJob | null): job is LastAppliedJob =>
  job?.status === "open" && job.stage === JobCandidateStageEnum.APPLIED;

// Stages where the BE rejects a working interview (pro already in the shift/offer pipeline).
const WORKING_INTERVIEW_BLOCKED_STAGES = new Set<JobCandidateStageEnum>([
  JobCandidateStageEnum.SHIFT_OFFERED,
  JobCandidateStageEnum.SHIFT_SCHEDULED,
  JobCandidateStageEnum.JOB_OFFERED,
  JobCandidateStageEnum.ACCEPTED,
]);

export const canScheduleWorkingInterview = (stage?: JobCandidateStageEnum | null): boolean =>
  stage == null || !WORKING_INTERVIEW_BLOCKED_STAGES.has(stage);

export const countScreeningCriteriaMet = (countTotal: number, failedCriteria: ReadonlyArray<unknown>): number =>
  countTotal - failedCriteria.length;

export const parseEditJob = (job: JobPostingResponse): JobPost => ({
  ...job,
  // generated status widens to pending/undefined; edit form only knows open/filled/archived
  status: job.status === "filled" || job.status === "archived" ? job.status : "open",
  wage: job.wage ?? 0,
  job_type_id: job?.job_type?.id ?? job?.job_type_id,
  experience_level_id: job?.experience_level_id ?? job?.experience_level?.id,
  // Grandfathered postings predating the field come back undefined; treat as OFF (DB default).
  requires_screening_questionnaire: job.requires_screening_questionnaire ?? false,
});

export const mapJobApplicantToApplicantData = (jobApplicant: JobApplicantResponse): ApplicantData => {
  const { candidate, stage, match_score, is_preferred_pro, shifts_at_location, screening_interview, interview } =
    jobApplicant;
  const { user, first_name, last_name, phone_number } = candidate;

  return {
    interview,
    id: jobApplicant.id,
    candidate_id: candidate.id,
    first_name,
    last_name,
    phone_number,
    pro_availability: jobApplicant.pro_availability,
    created_ts: jobApplicant.created_ts,
    applied_ts: jobApplicant.applied_ts,
    offered_directly: jobApplicant.offered_directly,
    screening_interview,
    // `user` is typed non-null by the generated schema, but the API returns null for
    // candidates with no linked user account (e.g. deactivated pros), so guard every access.
    user_id: user?.id,
    // API field named image_public_url but actually returns Cloudinary public ID (source: headshot_public_id)
    image_id: user?.image_public_url ?? null,
    stage,
    // API returns match_score as 0-10, UI expects percentage 0-100
    match_percentage: match_score == null ? undefined : match_score * 10,
    badges: dropTopProBadge(user?.badges),
    is_preferred_pro,
    is_verified: user?.is_verified ?? false,
    star_rating: user?.avg_rating ?? undefined,
    shifts_done: user?.shifts_completed ?? undefined,
    completion_rate: user?.completion_percentage == null ? undefined : Math.round(user.completion_percentage * 100),
    shifts_at_location: shifts_at_location > 0 ? shifts_at_location : undefined,
    prospect_notes: jobApplicant.prospect_notes ?? null,
    screening: jobApplicant.screening ?? null,
    hire_fee: jobApplicant.hire_fee,
    is_external: jobApplicant.is_external,
    invite: jobApplicant.invite,
    contact_shared: jobApplicant.contact_shared,
    worked_with_you: jobApplicant.worked_with_you,
    worked_with_you_history: jobApplicant.worked_with_you_history,
  };
};

/** "3 shifts here · 4.8 avg · Favorite": what the location already knows about this pro. */
export const formatWorkedWithYouHistory = ({
  shifts_in_position,
  average_rating,
  is_favorite,
}: WorkedWithYouHistory) => {
  const parts: Array<string> = [];
  if (shifts_in_position > 0) parts.push(`${shifts_in_position} ${shifts_in_position === 1 ? "shift" : "shifts"} here`);
  if (average_rating != null) parts.push(`${average_rating.toFixed(1)} avg`);
  if (is_favorite) parts.push("Favorite");
  return parts.join(" · ");
};

export type TimelineEvent =
  | { type: "interview"; data: TimelineInterview }
  | { type: "screening_interview"; data: TimelineScreeningInterview }
  | { type: "ai_screening"; data: JobApplicantAIScreening }
  | { type: "job_offer"; data: TimelineJobOffer };

// Interviews sort by start; offers and AI screenings have none, so fall back to created/invited.
const timelineSortKey = (event: TimelineEvent): Date => {
  switch (event.type) {
    case "job_offer":
      return event.data.created_ts;
    case "ai_screening":
      return event.data.invited_at;
    default:
      return event.data.start_time;
  }
};

export const combineTimelineEvents = (
  interviews: ReadonlyArray<TimelineInterview>,
  screeningInterviews: ReadonlyArray<TimelineScreeningInterview>,
  aiScreenings: ReadonlyArray<JobApplicantAIScreening>,
  jobOffers: ReadonlyArray<TimelineJobOffer>,
): Array<TimelineEvent> =>
  [
    ...interviews.map((interview) => ({ type: "interview" as const, data: interview })),
    ...screeningInterviews.map((interview) => ({ type: "screening_interview" as const, data: interview })),
    ...aiScreenings.map((aiScreening) => ({ type: "ai_screening" as const, data: aiScreening })),
    ...jobOffers.map((jobOffer) => ({ type: "job_offer" as const, data: jobOffer })),
  ].toSorted((a, b) => timelineSortKey(a).getTime() - timelineSortKey(b).getTime());

export const getActiveJobOffer = (events: Array<TimelineEvent>): TimelineJobOffer | undefined => {
  const activeOffer = events.find((e) => e.type === "job_offer" && e.data.status === "SENT");
  return activeOffer?.type === "job_offer" ? activeOffer.data : undefined;
};
